import { useEffect, useState } from "react";
import { RESPONDER_ROLES, type Responder } from "@server/incidents/schema";
import { ApiError, useResponders, useSaveResponders } from "../api/client";
import { Button, ErrorBanner, Select } from "../components/ui";
import { WidePanel } from "../components/Workspace";

const ROLE_LABELS: Record<Responder["role"], string> = {
  owner: "Owner",
  backup: "Backup owner",
  notify: "Notified",
};
const LABEL_TO_ROLE = Object.fromEntries(Object.entries(ROLE_LABELS).map(([role, label]) => [label, role])) as Record<string, Responder["role"]>;

const BLANK: Responder = { email: "", name: "", role: "notify" };

// Who is told about every incident. Changes apply to the next incident without a deploy.
export function IncidentResponders() {
  const stored = useResponders();
  const save = useSaveResponders();
  const [rows, setRows] = useState<Responder[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (stored.data) {
      setRows(stored.data.responders);
    }
  }, [stored.data]);

  const change = (index: number, patch: Partial<Responder>): void => {
    setSaved(false);
    setRows((current) => current.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  };

  const error =
    save.error instanceof ApiError
      ? save.error.issues.length > 0
        ? [...new Set(save.error.issues)].join(". ")
        : save.error.message
      : save.error
        ? "Something went wrong. Please try again."
        : undefined;

  return (
    <WidePanel>
      <div className="phase-head">
        <h1>Incident responders</h1>
        <p>
          Everyone listed is added to each incident channel and mentioned in #incident. The owner is assigned the Jira ticket and does the follow-up on the incident's page: the owner or
          backup owner marks it resolved, confirms the type, and ticks the write-up and lessons. The owner is messaged a week after it is resolved if any of that is still open. Each person
          must have a Slack account, and the owner a Jira account, under the email entered here.
        </p>
      </div>

      {stored.isError && <ErrorBanner>We couldn't load the responder list.</ErrorBanner>}
      {stored.data && (
        <form
          className="issue-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate({ responders: rows.map((row) => ({ ...row, email: row.email.trim().toLowerCase(), name: row.name.trim() })), version: stored.data.version }, { onSuccess: () => setSaved(true) });
          }}
        >
          {rows.length === 0 && <p className="record-note">No responders yet. Add an owner and a backup to open the incident form.</p>}
          {rows.map((row, index) => (
            <div className="field-row responder-row" key={index}>
              <div className="field">
                <input type="email" aria-label="Email" placeholder="name@vestd.com" value={row.email} onChange={(e) => change(index, { email: e.target.value })} />
              </div>
              <div className="field">
                <input type="text" aria-label="Name" placeholder="Name" value={row.name} onChange={(e) => change(index, { name: e.target.value })} />
              </div>
              <div className="field">
                <Select
                  aria-label="Role"
                  required
                  value={ROLE_LABELS[row.role]}
                  onChange={(e) => change(index, { role: LABEL_TO_ROLE[e.target.value] })}
                  options={RESPONDER_ROLES.map((role) => ROLE_LABELS[role])}
                />
              </div>
              <Button type="button" variant="secondary" className="btn-sm" onClick={() => setRows((current) => current.filter((_, position) => position !== index))}>
                Remove
              </Button>
            </div>
          ))}

          {error && <ErrorBanner>{error}</ErrorBanner>}
          {saved && <p className="record-note">Saved. The next incident uses this list.</p>}
          {stored.data.updatedBy && (
            <p className="hint">
              Last changed by {stored.data.updatedBy}
              {stored.data.updatedAt ? ` on ${new Date(stored.data.updatedAt).toLocaleString("en-GB")}` : ""}.
            </p>
          )}
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setRows((current) => [...current, BLANK])}>
              Add a person
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save the list"}
            </Button>
          </div>
        </form>
      )}
    </WidePanel>
  );
}
